// Quelle: /opt/lilasp-zeitplan/frontend/src/screens/Anmelden.tsx (Stand 2026-09-30)
// Geändert (SPEC 03 §9.1): Texte; Wortmarke + Produktwort „Handschrift“; „angemeldet bleiben“
// steht VORGABE AUS (Kinderdaten auf geteilten Bürorechnern, R03-17); nach dem Einlösen:
// angemeldet(), ["ich"] ungültig, dann zu sicheresZiel(?next=); Anfordern und „erneut senden“
// teilen sich EINE Funktion `anfordern(erneut)`, die zusätzlich ohne Wertmarke gar nicht erst
// abschickt (jeder Fehlversuch zählt serverseitig als captcha_fail gegen das Büro-Kontingent).
// Turnstile ist wieder da (E18: öffentlich + Turnstile wie Fragen/Projekte/Zeitplan) — Sitekey
// aus GET /auth/config, explizites render() mit action "login" (das Backend prüft die Aktion),
// die Wertmarke geht als Feld `turnstile` an POST /auth/start. Ohne Sitekey (Entwicklung)
// bleibt der Behälter ohne Höhe und der Knopf frei.
// ============================================================================
// VIER FALLEN, alle schon einmal zugeschlagen, hier gelöst — nicht wegräumen:
//
// (a) Der Doppelabsende-Riegel liegt in einem REF, nicht im Zustand: das Autofill des
//     Telefons löst `submit` UND `click` im selben Tick aus, und ein Zustandswert wäre beim
//     zweiten Aufruf noch der alte.
// (b) Der Turnstile-Effekt darf bei `schritt === "code"` NICHT aussteigen. Sonst räumt sein
//     Cleanup das Widget ab, und „Code erneut senden“ schickt für immer dieselbe, verbrauchte
//     Marke los (Cloudflare: timeout-or-duplicate) — was serverseitig jedes Mal eine
//     captcha_fail-Zeile gegen das IP-Kontingent des ganzen Büros schreibt.
// (c) `pruefungZurueck()` läuft nach JEDEM Absenden, auch nach Erfolg: eine Wertmarke gilt
//     genau einmal.
// (d) Nur das ERSTE Codefeld trägt `autoComplete="one-time-code"` — iOS füllt dort den ganzen
//     Code ein, `codeSetzen()` verteilt ihn weiter.
//
// Der Behälter heißt `pruefung` (BEHAELTER_ID), NIEMALS `turnstile`: der Browser legt für jedes
// Element mit id eine gleichnamige Eigenschaft auf `window` an, und `window.turnstile` wäre dann
// das <div> — die api.js hielte sich für schon geladen und zeichnete nichts.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Btn, Icon, Input, IOSwitch } from "../components/ui";
import { Logo } from "../components/app/Logo";
import { codeAnfordern, codeEinloesen, holeAuthConfig } from "../api/client";
import { ApiError } from "../lib/fetchJson";
import { codeRuecktaste, codeSetzen, codeText, codeVollstaendig, leererCode, CODE_LAENGE } from "../lib/code";
import { BEHAELTER_ID, ladeTurnstile } from "../lib/turnstile";
import { TX } from "../lib/texte";
import { sicheresZiel } from "../lib/ziel";
import { useTitel } from "../hooks/useTitel";
import { useAuthStore } from "../store/useAuthStore";

type Schritt = "email" | "code";

const ERNEUT_SPERRE_S = 60;

export function Anmelden() {
  useTitel(TX.anmelden.titel);
  const navigate = useNavigate();
  const [such] = useSearchParams();
  const ziel = sicheresZiel(such.get("next"));
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);

  const [schritt, setSchritt] = useState<Schritt>("email");
  const [email, setEmail] = useState("");
  // Vorbelegt AUS (R03-17): Kinderdaten, geteilte Bürorechner.
  const [bleiben, setBleiben] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState<string[]>(leererCode);
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [wackelt, setWackelt] = useState(false);
  const [erneutAb, setErneutAb] = useState(0);
  const [jetzt, setJetzt] = useState(() => Date.now());

  const [sitekey, setSitekey] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<string | undefined>(undefined);

  // (a) Der Doppelabsende-Riegel. Ref, nicht Zustand.
  const laeuftRef = useRef(false);
  const feldRefs = useRef<(HTMLInputElement | null)[]>([]);

  /* --- Turnstile -------------------------------------------------------- */
  useEffect(() => {
    let lebt = true;
    holeAuthConfig()
      .then((c) => {
        if (!lebt) return;
        setSitekey(c.turnstile_sitekey);
        setDomains(c.allowed_domains ?? []);
      })
      .catch(() => {
        // Ohne Konfiguration bleibt der Knopf frei und der Hausvorgabe-Satz stehen; scheitert
        // die Prüfung serverseitig, kommt der Grund als deutscher Satz zurück.
        if (lebt) setSitekey(null);
      });
    return () => {
      lebt = false;
    };
  }, []);

  // (b) Schritt 1 UND 2 brauchen ein lebendes Widget.
  useEffect(() => {
    if (!sitekey) return;
    let lebt = true;
    let id: string | undefined;
    ladeTurnstile()
      .then((api) => {
        if (!lebt || !document.getElementById(BEHAELTER_ID)) return;
        // Explizites render(): mit render=explicit zeichnet Cloudflare von sich aus nichts mehr,
        // und der Sitekey kommt ohnehin erst zur Laufzeit aus GET /auth/config.
        id = api.render(`#${BEHAELTER_ID}`, {
          sitekey,
          action: "login",
          theme: "auto",
          callback: (t) => setToken(t),
          "error-callback": () => {
            setToken(null);
            setFehler(TX.anmelden.pruefungFehler);
          },
          "expired-callback": () => setToken(null),
        });
        widgetRef.current = id;
      })
      .catch((e: unknown) => {
        if (lebt) setFehler(e instanceof Error ? e.message : TX.anmelden.pruefungFehler);
      });
    return () => {
      lebt = false;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* schon weg */
        }
      }
      widgetRef.current = undefined;
    };
  }, [sitekey, schritt]);

  /** (c) Nach JEDEM Absenden: die Wertmarke ist verbraucht. */
  const pruefungZurueck = useCallback(() => {
    setToken(null);
    if (widgetRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetRef.current);
      } catch {
        /* Widget ist weg — dann gibt es auch nichts zurückzusetzen */
      }
    }
  }, []);

  // Mit Sitekey geht ohne Wertmarke nichts raus: der Server lehnte ab (403) und schriebe eine
  // captcha_fail-Zeile gegen das IP-Kontingent des Büros.
  const ohneMarke = !!sitekey && !token;

  /* --- Countdown „Code erneut senden“ ----------------------------------- */
  useEffect(() => {
    if (!erneutAb) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 500);
    return () => clearInterval(uhr);
  }, [erneutAb]);
  const restS = erneutAb ? Math.max(0, Math.ceil((erneutAb - jetzt) / 1000)) : 0;

  /* --- Schritt 1: Code anfordern ---------------------------------------- */
  const anfordern = async (erneut: boolean) => {
    if (laeuftRef.current) return;
    if (erneut && restS > 0) return;
    if (ohneMarke) return;
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler("");
    try {
      const a = await codeAnfordern(email.trim(), token);
      setChallenge(a.challenge);
      setCode(leererCode());
      setSchritt("code");
      setErneutAb(Date.now() + ERNEUT_SPERRE_S * 1000);
      setJetzt(Date.now());
      pruefungZurueck(); // (c) auch nach Erfolg
      setTimeout(() => feldRefs.current[0]?.focus(), 60);
    } catch (e) {
      setFehler(e instanceof ApiError ? e.message : TX.anmelden.startFehler);
      pruefungZurueck(); // (c)
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  };

  /* --- Schritt 2: Code einlösen ----------------------------------------- */
  const absendenCode = useCallback(
    async (ziffern: string[]) => {
      // Ein Code ist EINMAL gültig. Ohne diesen Riegel schickt das Autofill des Telefons ihn
      // zweimal, der zweite Versuch läuft ins Leere und die Person liest „Der Code stimmt
      // nicht.“, obwohl alles stimmte.
      if (laeuftRef.current) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        await codeEinloesen(challenge, codeText(ziffern), bleiben);
        angemeldet();
        await qc.invalidateQueries({ queryKey: ["ich"] });
        navigate(ziel, { replace: true });
      } catch (e) {
        setFehler(e instanceof ApiError ? e.message : TX.anmelden.codeFehler);
        setCode(leererCode());
        setWackelt(true);
        setTimeout(() => setWackelt(false), 320);
        setTimeout(() => feldRefs.current[0]?.focus(), 40);
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [challenge, bleiben, angemeldet, qc, navigate, ziel],
  );

  const ziffernSetzen = (index: number, roh: string) => {
    // Tippt jemand in ein schon gefülltes Kästchen, steht dort kurz "12". Gemeint ist die NEUE
    // Ziffer, nicht ein zweistelliger Einschub — sonst rutscht die alte Ziffer nach rechts.
    const alt = code[index] ?? "";
    const eingabe = alt && roh.length === 2 && roh.startsWith(alt) ? roh.slice(1) : roh;
    const { code: neu, fokus } = codeSetzen(code, index, eingabe);
    setCode(neu);
    feldRefs.current[fokus]?.focus();
    // Die sechste Ziffer schickt die Karte selbst ab.
    if (codeVollstaendig(neu)) void absendenCode(neu);
  };

  // Wer schon angemeldet ist, hat hier nichts verloren.
  useEffect(() => {
    if (status === "angemeldet") navigate(ziel, { replace: true });
  }, [status, navigate, ziel]);

  const domainHinweis = TX.anmelden.domains(domains.length ? [...domains] : ["lilasp.de", "lilasp.gmbh"]);

  return (
    <div className="anmelden-hof">
      <div className="anmelden-marke">
        <Logo />
        <span className="marke-kachel" aria-hidden="true">
          <Icon name="hand-waving" size={17} />
        </span>
        <span className="marke-name disp">{TX.allgemein.produkt}</span>
      </div>

      <main className="card anmelden-karte karte-ein">
        {schritt === "email" && (
          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              void anfordern(false);
            }}
          >
            <h1>{TX.anmelden.titel}</h1>
            <p className="unterzeile">{TX.anmelden.unterzeile}</p>

            <label className="lbl" htmlFor="hs-email">
              {TX.anmelden.email}
            </label>
            <Input
              id="hs-email"
              type="email"
              value={email}
              autoFocus
              required
              autoComplete="email"
              inputMode="email"
              placeholder={TX.anmelden.emailPlatzhalter}
              onChange={(ev) => setEmail(ev.target.value)}
            />
            <p className="hinweiszeile">{domainHinweis}</p>

            <div className="reihe" style={{ justifyContent: "space-between", margin: "18px 0 4px", gap: 12 }}>
              <label htmlFor="hs-bleiben" style={{ fontSize: 13, fontWeight: 600 }}>
                {TX.anmelden.bleiben}
              </label>
              <IOSwitch id="hs-bleiben" on={bleiben} onChange={setBleiben} aria-label={TX.anmelden.bleiben} />
            </div>

            {/* (Behälter) pruefung, NIE turnstile. Ohne Sitekey bleibt er ohne Höhe und der
                Knopf ist frei. */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0, marginTop: sitekey ? 10 : 0 }} />

            <p className="fehlerzeile" role="status">
              {fehler}
            </p>

            <Btn
              type="submit"
              size="lg"
              style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
              disabled={laeuft || !email.trim() || ohneMarke}
            >
              <Icon name="paper-plane-tilt" size={17} />
              {TX.anmelden.codeSchicken}
            </Btn>
          </form>
        )}

        {schritt === "code" && (
          <form
            className={wackelt ? "wackel" : ""}
            onSubmit={(ev) => {
              ev.preventDefault();
              if (codeVollstaendig(code)) void absendenCode(code);
            }}
          >
            <h1>{TX.anmelden.codeTitel}</h1>
            <p className="unterzeile">{TX.anmelden.codeUnterzeile}</p>
            <p className="mono dim" style={{ fontSize: 12, margin: "-12px 0 16px", overflowWrap: "anywhere" }}>
              {TX.anmelden.gesendetAn(email.trim())}
            </p>

            <div className="code-reihe" role="group" aria-label={TX.anmelden.codeTitel}>
              {code.map((z, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    feldRefs.current[i] = el;
                  }}
                  className={`codebox${codeVollstaendig(code) ? " puls" : ""}`}
                  value={z}
                  inputMode="numeric"
                  // (d) Nur das erste Feld trägt one-time-code.
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  autoFocus={i === 0}
                  maxLength={CODE_LAENGE}
                  aria-label={TX.anmelden.ziffer(i + 1, CODE_LAENGE)}
                  disabled={laeuft}
                  onChange={(ev) => ziffernSetzen(i, ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Backspace") {
                      ev.preventDefault();
                      const { code: neu, fokus } = codeRuecktaste(code, i);
                      setCode(neu);
                      feldRefs.current[fokus]?.focus();
                    }
                    if (ev.key === "ArrowLeft") feldRefs.current[Math.max(0, i - 1)]?.focus();
                    if (ev.key === "ArrowRight") feldRefs.current[Math.min(CODE_LAENGE - 1, i + 1)]?.focus();
                  }}
                  onPaste={(ev) => {
                    ev.preventDefault();
                    ziffernSetzen(i, ev.clipboardData.getData("text"));
                  }}
                />
              ))}
            </div>

            {/* Dasselbe <div> wie in Schritt 1 — immer nur EINES im Baum, sonst gäbe es die
                Kennung zweimal. Der Effekt oben zeichnet hierher ein frisches Widget, damit
                „Code erneut senden“ eine unverbrauchte Marke hat (Falle b). */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0, marginTop: sitekey ? 14 : 0 }} />

            <p className="fehlerzeile" role="status" aria-busy={laeuft || undefined} style={{ textAlign: "center" }}>
              {laeuft ? "" : fehler}
            </p>

            <div className="reihe wrap" style={{ justifyContent: "space-between", marginTop: 10 }}>
              <Btn
                variant="ghost"
                size="sm"
                type="button"
                icon="arrow-left"
                onClick={() => {
                  setSchritt("email");
                  setFehler("");
                }}
              >
                {TX.anmelden.andereAdresse}
              </Btn>
              <Btn variant="ghost" size="sm" type="button" icon="arrows-clockwise" disabled={restS > 0 || laeuft || ohneMarke} onClick={() => void anfordern(true)}>
                {restS > 0 ? TX.anmelden.erneutIn(restS) : TX.anmelden.erneut}
              </Btn>
            </div>
          </form>
        )}
        <div className="anmelden-papier" aria-hidden="true" />
      </main>

      <p className="anmelden-fuss">
        <Icon name="shield-check" size={14} />
        {TX.anmelden.zugang}
      </p>
    </div>
  );
}

export default Anmelden;
